/**
 * Mixpanel User Property Sync
 * 
 * Syncs user subscription and plan information to Mixpanel user properties
 */

import { supabase } from '@/integrations/supabase/client';
import { safeMixpanelPeopleSet } from '@/lib/mixpanel-utils';

export interface SubscriptionData {
  plan_type: string | null;
  subscription_status: string | null;
  is_subscribed: boolean;
  is_trialing: boolean;
  subscription_tier?: string | null;
}

/**
 * Sync user plan and subscription status to Mixpanel
 * This should be called whenever subscription status changes
 */
export async function syncUserPlanToMixpanel(userId: string): Promise<void> {
  try {
    // Get subscription status from database
    const { data, error } = await supabase
      .rpc('get_user_subscription_status', { p_user_id: userId })
      .single();

    if (error) {
      // If no subscription found, user is on free plan
      if (error.code === 'PGRST116') {
        updateMixpanelUserProperties({
          plan_type: null,
          subscription_status: 'none',
          is_subscribed: false,
          is_trialing: false,
        });
        return;
      }
      console.warn('Failed to fetch subscription status for Mixpanel sync:', error);
      return;
    }

    // Update Mixpanel user properties
    updateMixpanelUserProperties({
      plan_type: data.plan_type || null,
      subscription_status: data.subscription_status || 'none',
      is_subscribed: data.is_subscribed || false,
      is_trialing: data.is_trialing || false,
      subscription_tier: data.subscription_tier || null,
    });
  } catch (error) {
    console.warn('Error syncing user plan to Mixpanel:', error);
  }
}

/**
 * Update Mixpanel user properties with subscription data
 */
function updateMixpanelUserProperties(data: SubscriptionData): void {
  // Determine plan name for Mixpanel
  let planName: string;
  if (data.is_subscribed) {
    if (data.plan_type === 'annual' || data.plan_type === 'yearly') {
      planName = 'Pro Annual';
    } else if (data.plan_type === 'monthly') {
      planName = 'Pro Monthly';
    } else {
      planName = 'Pro';
    }
  } else if (data.is_trialing) {
    planName = 'Free Trial';
  } else {
    planName = 'Free';
  }

  // Update Mixpanel user properties (safely)
  safeMixpanelPeopleSet({
    plan: planName,
    plan_type: data.plan_type || 'none',
    subscription_status: data.subscription_status || 'none',
    is_subscribed: data.is_subscribed,
    is_trialing: data.is_trialing,
    subscription_tier: data.subscription_tier || 'free',
  });
}
